<script lang="ts">
  import { glossary } from '../../store';
  import { players } from '../models/player.type';
  import { enemies, special, bosses } from '../models/enemy.type';
  import { techniques, techniqueKind, type TechniqueKind } from '../models/techniques.type';
  import { faces } from '../models/face.type';
  import {
    getDescriptions,
    listStatusEffects,
    StatusEffectRegistry,
  } from '../models/status-effects';
  import { ModifierTags } from '../models/modifier.type';
  import { TraitRegistry } from '../models/trait-registry';
  import { consumables } from '../models/items/consumables';
  import { dies } from '../models/items/dies';
  import { specials as itemSpecials } from '../models/items/specials';
  import { potions } from '../models/items/potions';
  import { dieCollection } from '../models/die.type';
  import { environments } from '../models/environment.type';
  import type { Die as DieObject } from '../models/die.type';
  import { createDie } from '../models/die.type';
  import DieElement from '../dice-tray/die.svelte';

  import Die from './components/die.svelte';
  import Classes from './components/classes.svelte';
  import Heroes from './components/heroes.svelte';
  import Enemy from './components/enemy.svelte';
  import Technique from './components/technique.svelte';
  import Item from './components/item.svelte';
  import Environment from './components/environment.svelte';
  import Modifier from './components/modifier.svelte';
  import Gameplay from './components/gameplay.svelte';
  import Face from './components/face.svelte';
  import Icon from '../icon/icon.svelte';
  import Text from '../components/text.svelte';
  import { playAudio, SoundType } from '../components/audio.svelte';

  import { clone } from '../utils/utils';

  var glossaryOpen = $state(true);
  glossary.open.subscribe((val) => {
    glossaryOpen = val;
    if (glossaryOpen) {
      playAudio(SoundType.ManualOpen);
    }
  });

  type Category = {
    title: string;
    data?: any[];
    search?: boolean;
    /** Optional note shown once above the list, for sections whose entries need
     * a shared bit of context that would be noise repeated on every row. */
    intro?: string;
  };

  // Raw: a category carries its whole list of definitions, and those are only
  // ever swapped out, never edited, so there is nothing to gain from proxying them.
  let selected: Category = $state.raw({ title: '' });
  let searchTerm: string = $state('');
  let tags: Array<string> = $state.raw([]);
  let searchDie: Array<DieObject> = $state.raw([]);
  let searchKind: TechniqueKind | null = $state(null);

  let heroes: Array<any> = [];
  players.forEach((player) => {
    player.archetypes.forEach((archetype, index) => {
      if (!player.dice1 || !player.dice2 || !player.dice3) {
        return;
      }
      heroes.push({
        title: archetype.title,
        text: archetype.text,
        id: archetype.id,
        colour: player.colour,
        // Carried through so a hero entry can name the class it belongs to and
        // link back to it -- the Classes section links the other way.
        classTitle: player.title,
        classType: player.type,
        complexity: archetype.complexity,
        dice: [player.dice1[index], player.dice2[index], player.dice3[index]],
      });
    });
  });

  // An unregistered type used to fall through as `{}`, which sorts against an
  // undefined title and hands the row component an entry with no tags to read.
  // Nothing hits it today -- all 46 are registered -- so drop it rather than
  // render a blank.
  const statusEffects = listStatusEffects()
    .map((type) => {
      const ModClass = StatusEffectRegistry.get(type);
      if (!ModClass) return null;
      const modInstance = new ModClass();
      return {
        title: modInstance.title,
        text: modInstance.text,
        icon: modInstance.icon,
        tags: modInstance.tags,
        // Kept so the row can report which lifecycle hooks the effect
        // implements, i.e. when it actually fires.
        instance: modInstance,
      };
    })
    .filter((effect) => effect !== null);

  // Who carries each trait, so its row can say. A hero's archetype id is the
  // type of the trait it grants, so heroes and enemies key the same way.
  // Filled once during setup and never changed, so it needs no reactivity.
  // eslint-disable-next-line svelte/prefer-svelte-reactivity
  const carriers = new Map<string, Array<string>>();
  function carry(type: string, who: string) {
    carriers.set(type, [...(carriers.get(type) ?? []), who]);
  }
  players.forEach((player) => {
    player.archetypes.forEach((archetype) => carry(archetype.id, archetype.title));
  });
  [...enemies, ...special, ...bosses].forEach((enemy) => {
    (enemy.traits ?? []).forEach((type) => carry(type, enemy.title));
  });

  // Read off the registered classes rather than constructed, as enemy.svelte
  // does: a Trait wants an owner, and init() runs on construction. The hooks the
  // row reports timings from live on the prototype, so that stands in.
  const traits = TraitRegistry.getAll().map(([type, TraitClass]) => ({
    title: TraitClass.baseTitle ?? type,
    text: TraitClass.baseText ?? '',
    icon: TraitClass.baseIcon ?? 'trait',
    tags: carriers.get(type) ?? [],
    instance: TraitClass.prototype,
  }));

  const boonCount = statusEffects.filter((effect) =>
    effect.tags?.includes(ModifierTags.Boon),
  ).length;
  const baneCount = statusEffects.filter((effect) =>
    effect.tags?.includes(ModifierTags.Bane),
  ).length;

  const categories: Array<Category> = [
    {
      title: 'Gameplay',
      data: [0],
    },
    {
      title: 'Classes',
      data: players,
    },
    {
      title: 'Heroes',
      search: true,
      data: heroes,
    },
    {
      title: 'Die',
      search: true,
      intro:
        'Every die is cut with ten faces but only ever shows six of them. ' +
        'Upgrading a die slides that window one step towards its stronger ' +
        'faces -- it loses its weakest and gains the next one up. Downgrading ' +
        'slides it back the other way.',
      data: Object.keys(dieCollection)
        .map((key) => {
          var die = clone(dieCollection[key]);
          die.title = key;
          return die;
        })
        .sort((a, b) => {
          if (a.title < b.title) {
            return -1;
          }
          if (a.title > b.title) {
            return 1;
          }
          return 0;
        }),
    },
    {
      title: 'Faces',
      search: true,
      intro:
        'The symbols a die can roll. Each face lists the tags it carries, ' +
        'which dice it appears on, and how many techniques call for it.',
      data: faces.sort((a, b) => {
        if (a.id < b.id) {
          return -1;
        }
        if (a.id > b.id) {
          return 1;
        }
        return 0;
      }),
    },
    {
      title: 'Status Effects',
      search: true,
      intro:
        'Boons and banes that sit on a character and stack. Each one lists ' +
        'when it fires -- a stack of Poison does nothing until the end of the ' +
        'turn, while Block acts the moment damage lands.',
      // Deliberately not cloned. clone() is JSON round-tripping, which drops the
      // prototype -- and with it every lifecycle method the row reads to work
      // out when the effect fires. The rows are only ever read, so the instances
      // are handed over as they are.
      data: [...statusEffects].sort((a, b) => {
        if (a.title < b.title) {
          return -1;
        }
        if (a.title > b.title) {
          return 1;
        }
        return 0;
      }),
    },
    {
      title: 'Traits',
      search: true,
      intro:
        "Abilities that stay with a character for the whole run. Every hero's " +
        'archetype grants one, and some enemies carry their own. Each lists who ' +
        'has it and when it fires.',
      data: [...traits].sort((a, b) => {
        if (a.title < b.title) {
          return -1;
        }
        if (a.title > b.title) {
          return 1;
        }
        return 0;
      }),
    },
    {
      title: 'Enemies',
      search: true,
      intro:
        'Everything you can be made to fight. Bosses and special encounters ' +
        'are marked; the rest are the common roster. Each entry lists the dice ' +
        'it rolls and where it turns up.',
      // Which of the three lists a row came from is lost the moment they are
      // concatenated and sorted into one alphabetical run, so it is stamped on
      // here for the row to show.
      data: [
        ...enemies.map((enemy) => ({ ...enemy, tier: 'enemy' })),
        ...special.map((enemy) => ({ ...enemy, tier: 'special' })),
        ...bosses.map((enemy) => ({ ...enemy, tier: 'boss' })),
      ].sort((a, b) => {
        if (a.title < b.title) {
          return -1;
        }
        if (a.title > b.title) {
          return 1;
        }
        return 0;
      }),
    },
    {
      title: 'Techniques',
      search: true,
      intro:
        'Recipes: lock the faces on the left and you can spend them on the ' +
        'action named on the right. Ordered by ingredients, so recipes that ' +
        'share faces sit together. Search matches names, tags and faces.',
      data: techniques.sort((a, b) => {
        if (a.ingredients.join() < b.ingredients.join()) {
          return -1;
        }
        if (a.ingredients.join() > b.ingredients.join()) {
          return 1;
        }
        return 0;
      }),
    },
    {
      title: 'Items',
      search: true,
      intro:
        'Carried in your inventory. Each one either adds a technique you can ' +
        'spend from the inventory, or hands you an extra die to roll.',
      // items.ts concatenates the four source files, and which one a definition
      // came from is its category -- lost on concat, so stamped on here.
      data: [
        ...consumables.map((item) => ({ ...item, category: 'Consumable' })),
        ...potions.map((item) => ({ ...item, category: 'Potion' })),
        ...dies.map((item) => ({ ...item, category: 'Die' })),
        ...itemSpecials.map((item) => ({ ...item, category: 'Special' })),
      ].sort((a, b) => {
        if (a.title < b.title) {
          return -1;
        }
        if (a.title > b.title) {
          return 1;
        }
        return 0;
      }),
    },
    {
      title: 'Environments',
      search: true,
      intro:
        'Where battles are fought. Each one draws from its own roster, so ' +
        'where you pick a fight decides what you end up fighting.',
      data: environments
        .filter((environment) => {
          return environment.title != 'Default';
        })
        .sort((a, b) => {
          if (a.title < b.title) {
            return -1;
          }
          if (a.title > b.title) {
            return 1;
          }
          return 0;
        }),
    },
  ];

  /** The scrolling list, so switching section can put you back at the top. */
  let content: HTMLElement | undefined = $state();

  function select(category: Category) {
    selected = category;
    tags = [];
    glossary.selected.set(category.title);
    searchTerm = '';

    // .content is one persistent element that every section renders into, so
    // without this a jump landed you at whatever offset the previous section
    // was scrolled to. Runs before the element exists on the initial select.
    if (content) {
      content.scrollTop = 0;
    }
  }
  select(categories[0]);

  function close() {
    glossary.open.set(false);
    playAudio(SoundType.ManualClose);
  }

  function toggleTag(tag: string) {
    if (tags.includes(tag)) {
      tags = tags.filter((t) => t !== tag);
    } else {
      tags = [...tags, tag];
    }
  }

  glossary.selected.subscribe((val) => {
    if (val) {
      categories.forEach((category) => {
        if (category.title.toLowerCase() == val.toLowerCase()) {
          select(category);
        }
      });
    }
  });

  glossary.searchTerm.subscribe((val) => {
    searchTerm = val;
  });

  glossary.searchDie.subscribe((val) => {
    if (val && val.length) {
      searchDie = val.map((die, index) => {
        return die;
      });
    } else {
      searchDie = [];
    }
  });

  glossary.searchKind.subscribe((val) => {
    searchKind = val;
  });

  const clearDie = () => {
    glossary.searchDie.set([]);
  };

  const clearKind = () => {
    glossary.searchKind.set(null);
  };

  const clearSearch = () => {
    searchTerm = '';
  };

  /**
   * Techniques are the one long section -- 168 of them -- and their titles are
   * flavour names ("Blot out the Sun", "Quick Thinking"), so a title-only match
   * only finds what you could already name. Matching the tags and ingredients
   * too means "burn", "heal" or "sword" all find something useful.
   */
  function matchesTechnique(technique: any, term: string) {
    const needle = term.toLowerCase();
    if (!needle) {
      return true;
    }

    return (
      technique.title.toLowerCase().includes(needle) ||
      (technique.tags ?? []).some((tag: string) => tag.toLowerCase().includes(needle)) ||
      (technique.ingredients ?? []).some((ingredient: string) =>
        ingredient.toLowerCase().includes(needle),
      )
    );
  }

  /**
   * Mirrors the per-section filter in the markup below -- Faces match on `id`,
   * everything else on `title` -- so the list can say when it has come up empty
   * instead of just showing blank space.
   *
   * Only counts the search-term filter. Techniques and Status Effects narrow
   * further inside their own components (by die and by tag), so a section can
   * still render nothing while this reports matches; the message is suppressed
   * rather than wrong in that case.
   */
  let searchMatches = $derived(
    selected.search && selected.data
      ? selected.data.filter((item) => {
          if (selected.title === 'Techniques') {
            return matchesTechnique(item, searchTerm);
          }
          const key = (selected.title === 'Faces' ? item.id : item.title) ?? '';
          return key.toLowerCase().includes(searchTerm.toLowerCase());
        }).length
      : -1,
  );

  function onKeydown(event: KeyboardEvent) {
    if (glossaryOpen && event.key === 'Escape') {
      close();
    }
  }
</script>

<svelte:window onkeydown={onKeydown} />

{#if glossaryOpen}
  <!-- Decorative only. The modals in lib/modals don't close on a backdrop click
       either, and losing your place in a long reference by clipping the edge of
       the panel would be a poor trade. Escape and the close button cover it. -->
  <div class="scrim" aria-hidden="true"></div>

  <div class="container">
    <div class="header">
      <h1>Glossary</h1>
      <button class="close" onclick={close} aria-label="Close glossary">
        <Icon name="broken" />
      </button>
    </div>

    <nav class="categories" aria-label="Glossary sections">
      {#each categories as category}
        <button
          class="category"
          class:selected={selected.title === category.title}
          aria-current={selected.title === category.title ? 'true' : undefined}
          onclick={() => select(category)}
        >
          {category.title}
        </button>
      {/each}
    </nav>

    <div class="information">
      <div class="title">
        <h2>{selected.title}</h2>

        {#if selected.search}
          <div class="search">
            <input
              type="text"
              bind:value={searchTerm}
              placeholder="Search {selected.title.toLowerCase()}"
            />
            {#if searchTerm}
              <button class="clear" onclick={clearSearch} aria-label="Clear search">&times;</button>
            {/if}
          </div>
        {/if}
      </div>

      <div class="content" bind:this={content}>
        {#if selected.intro}
          <Text class="intro" data={selected.intro} />
        {/if}

        {#if selected.data && selected.data.length}
          {#if selected.title == 'Gameplay'}
            <Gameplay />
          {/if}

          {#if selected.title == 'Classes'}
            {#each selected.data as item}
              <Classes data={item} />
            {/each}
          {/if}

          {#if selected.title == 'Heroes'}
            {#each selected.data as item}
              {#if item.title.toLowerCase().includes(searchTerm.toLowerCase())}
                <Heroes data={item} />
              {/if}
            {/each}
          {/if}

          {#if selected.title == 'Die'}
            {#each selected.data as item}
              {#if item.title.toLowerCase().includes(searchTerm.toLowerCase())}
                <Die data={item} />
              {/if}
            {/each}
          {/if}

          {#if selected.title == 'Faces'}
            {#each selected.data as item}
              {#if item.id.toLowerCase().includes(searchTerm.toLowerCase())}
                <Face data={item} />
              {/if}
            {/each}
          {/if}

          {#if selected.title == 'Enemies'}
            {#each selected.data as item}
              {#if item.title.toLowerCase().includes(searchTerm.toLowerCase())}
                <Enemy data={item} />
              {/if}
            {/each}
          {/if}

          {#if selected.title == 'Status Effects'}
            <div class="tags">
              <button
                class="tag boon {tags.includes(ModifierTags.Boon) ? 'selected' : ''}"
                onclick={() => {
                  toggleTag(ModifierTags.Boon);
                }}>Boon <b>{boonCount}</b></button
              >
              <button
                class="tag bane {tags.includes(ModifierTags.Bane) ? 'selected' : ''}"
                onclick={() => {
                  toggleTag(ModifierTags.Bane);
                }}>Bane <b>{baneCount}</b></button
              >
            </div>
            {#each selected.data as item}
              {#if item.title.toLowerCase().includes(searchTerm.toLowerCase())}
                <Modifier data={item} {tags} />
              {/if}
            {/each}
          {/if}

          {#if selected.title == 'Traits'}
            {#each selected.data as item}
              {#if item.title.toLowerCase().includes(searchTerm.toLowerCase())}
                <Modifier data={item} />
              {/if}
            {/each}
          {/if}

          {#if selected.title == 'Techniques'}
            {#if searchDie.length}
              <div class="die-filter">
                <span class="die-filter-label"> Showing only techniques these dice can make </span>

                <div class="die-list">
                  {#each searchDie as die, index}
                    <div class="die-container">
                      <DieElement {die} />
                    </div>
                  {/each}

                  <button class="clear-die" onclick={clearDie}> Show all </button>
                </div>
              </div>
            {/if}

            {#if searchKind}
              <div class="die-filter">
                <span class="die-filter-label">Showing only {searchKind} techniques</span>
                <button class="clear-die" onclick={clearKind}> Show all </button>
              </div>
            {/if}

            {#each selected.data as item}
              {#if matchesTechnique(item, searchTerm) && (!searchKind || techniqueKind(item) === searchKind)}
                <Technique data={item} {searchDie} />
              {/if}
            {/each}
          {/if}

          {#if selected.title == 'Items'}
            {#each selected.data as item}
              {#if item.title.toLowerCase().includes(searchTerm.toLowerCase())}
                <Item data={item} />
              {/if}
            {/each}
          {/if}

          {#if selected.title == 'Environments'}
            {#each selected.data as environment}
              {#if environment.title.toLowerCase().includes(searchTerm.toLowerCase())}
                <Environment data={environment} />
              {/if}
            {/each}
          {/if}
        {/if}

        {#if searchTerm && searchMatches === 0}
          <p class="empty">No {selected.title.toLowerCase()} match "{searchTerm}".</p>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style lang="less">
  @import '../../defines.less';

  // The --background-colour trio is read right through the glossary (and by the
  // global button.less) but nothing defines it at the root -- it was commented
  // out of App.svelte and never restored, so every border and panel fill here
  // was resolving to nothing. Defined on the panel so it cascades into the
  // entry components without changing anything outside the glossary.
  .container {
    --background-colour: @panel;
    --background-colour-light: @panel-line;
    --background-colour-dark: @panel-sunken;

    --gold: @edge-shine;
    --gutter: 16px;
    --radius: 10px;
  }

  .scrim {
    position: fixed;
    inset: 0;
    z-index: 1;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(3px);
  }

  .container {
    position: relative;
    z-index: 2;
    // Centres in .full-screen, which is a flex column. Replaces a margin-top of
    // 10%, which pushed the panel off-centre on anything short.
    margin: auto;

    width: min(95vw, 1200px);
    // dvh rather than vh: mobile browser chrome shrinks the viewport and vh does
    // not follow it, so the bottom of the panel sat behind the URL bar.
    height: min(880px, calc(100dvh - 5rem));

    display: grid;
    grid-template-columns: 210px minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr);

    color: var(--light-font);
    background:
      radial-gradient(ellipse at top, rgba(105, 47, 31, 0.18), transparent 70%),
      var(--background-colour);
    border-radius: var(--radius);
    // Same ramp as the <Frame> border-image, so the glossary reads as part of
    // the same set as the modals.
    border: 3px solid transparent;
    border-image: linear-gradient(
        to top right,
        @edge-deep 0%,
        @edge-mid 20%,
        @edge-mid 50%,
        @edge-warm 80%,
        @edge-shine 90%,
        @edge-warm 100%
      )
      1;
    box-shadow: 0 18px 50px rgba(0, 0, 0, 0.65);

    @media @medium {
      grid-template-columns: 170px minmax(0, 1fr);
    }

    // Full-bleed on phones. A reference document is the one thing that should
    // take the whole screen rather than float in the middle of it.
    @media @mobile {
      width: 100vw;
      height: 100dvh;
      grid-template-columns: minmax(0, 1fr);
      grid-template-rows: auto auto minmax(0, 1fr);
      border-radius: 0;
      border-width: 0 0 3px 0;
      padding-top: env(safe-area-inset-top);
      padding-bottom: env(safe-area-inset-bottom);
    }
  }

  .header {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    grid-gap: var(--gutter);
    padding: 10px var(--gutter);
    background: linear-gradient(to bottom, var(--background-colour), var(--background-colour-dark));
    border-bottom: 1px solid var(--background-colour-light);

    h1 {
      // app.css sets h1 to 3.2em, which is far taller than this bar.
      font-size: 1.75rem;
      line-height: 1.2;
      letter-spacing: 0.06em;
      color: var(--gold);

      @media @mobile {
        font-size: 1.35rem;
      }
    }

    .close {
      // Reset of the global button.less pill -- this one is a bare icon.
      flex: 0 0 auto;
      // 44px is the smallest comfortable touch target; the old close was a bare
      // <div> wrapping the icon, so it was only as big as the glyph.
      width: 44px;
      height: 44px;
      padding: 8px;
      --icon-size: 28px;
      border-radius: 50%;
      background: none;
      box-shadow: none;
      opacity: 0.75;
      transition:
        opacity 0.2s,
        background 0.2s;

      &:hover,
      &:focus-visible {
        opacity: 1;
        background: rgba(255, 255, 255, 0.08);
        filter: none;
      }

      &:active {
        box-shadow: none;
        transform: scale(0.94);
      }
    }
  }

  .categories {
    display: flex;
    flex-direction: column;
    grid-gap: 2px;
    padding: 8px;
    overflow-y: auto;
    background: var(--background-colour-dark);
    border-right: 1px solid var(--background-colour-light);

    .category {
      // Also a button.less reset: these are list rows, not pills.
      flex: 0 0 auto;
      justify-content: flex-start;
      text-align: left;
      padding: 10px 12px;
      border-radius: 6px;
      background: none;
      box-shadow: none;
      font-size: 0.95rem;
      color: var(--light-font);
      opacity: 0.72;
      // The bar that marks the active row, laid in transparent up front so
      // switching sections does not shift the text sideways.
      border-left: 3px solid transparent;
      transition:
        background 0.2s,
        opacity 0.2s,
        border-color 0.2s;

      &:hover,
      &:focus-visible {
        opacity: 1;
        filter: none;
        background: rgba(255, 255, 255, 0.06);
      }

      &.selected {
        opacity: 1;
        color: var(--gold);
        background: var(--background-colour);
        border-left-color: @edge-warm;
        box-shadow: inset 0 0 0 1px var(--background-colour-light);
      }

      &:active {
        box-shadow: none;
      }
    }

    // A horizontally scrolling tab strip beats a 100px-wide column of wrapped
    // words, which is what the old layout collapsed to on a phone.
    @media @mobile {
      flex-direction: row;
      grid-gap: 6px;
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;
      border-right: none;
      border-bottom: 1px solid var(--background-colour-light);

      &::-webkit-scrollbar {
        height: 0;
      }

      .category {
        white-space: nowrap;
        min-height: 44px;
        border-left: none;
        border-bottom: 3px solid transparent;
        border-radius: 6px 6px 0 0;

        &.selected {
          border-left: none;
          border-bottom-color: @edge-warm;
        }
      }
    }
  }

  .information {
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    // Without this the grid row refuses to shrink and .content never scrolls.
    min-height: 0;
    overflow: hidden;

    .title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      grid-gap: var(--gutter);
      padding: 10px var(--gutter);
      background: var(--background-colour-dark);
      border-bottom: 1px solid var(--background-colour-light);

      h2 {
        font-size: 1.4rem;
        text-align: left;
        text-transform: capitalize;
        letter-spacing: 0.04em;
        color: var(--gold);
      }

      .search {
        position: relative;
        flex: 0 1 260px;

        input {
          // Nothing in the project sets box-sizing, so a content-box input at
          // width:100% came out 46px wider than the 260px it was given -- its
          // padding and border stack on top -- and ran off the panel edge,
          // taking the clear button with it.
          box-sizing: border-box;
          width: 100%;
          padding: 9px 32px 9px 12px;
          font-family: inherit;
          font-size: 0.9rem;
          color: var(--light-font);
          background: var(--background-colour);
          border: 1px solid var(--background-colour-light);
          border-radius: 999px;
          outline: none;
          transition:
            border-color 0.2s,
            box-shadow 0.2s;

          &::placeholder {
            color: var(--light-font);
            opacity: 0.45;
          }

          &:focus {
            border-color: @edge-warm;
            box-shadow: 0 0 0 2px rgba(135, 68, 43, 0.35);
          }
        }

        .clear {
          position: absolute;
          top: 50%;
          right: 4px;
          transform: translateY(-50%);
          width: 26px;
          height: 26px;
          padding: 0;
          font-size: 1.1rem;
          line-height: 1;
          background: none;
          box-shadow: none;
          opacity: 0.6;

          &:hover,
          &:focus-visible {
            opacity: 1;
            filter: none;
          }
        }
      }

      // Stack the heading above a full-width search rather than letting the two
      // squeeze each other.
      @media @mobile {
        flex-direction: column;
        align-items: stretch;
        grid-gap: 8px;

        h2 {
          text-align: center;
        }

        .search {
          flex: 1 1 auto;

          input {
            // Under 16px, iOS zooms the whole page in on focus.
            font-size: 16px;
            padding: 11px 40px 11px 14px;
          }

          .clear {
            width: 34px;
            height: 34px;
            right: 3px;
          }
        }
      }
    }

    .content {
      display: flex;
      flex-direction: column;
      // No gap. The entries are sections of one document now rather than a
      // stack of cards, so their own padding sets the rhythm and a rule marks
      // each join.
      grid-gap: 0;
      padding: var(--gutter);
      overflow-y: auto;
      // So a flick that runs off the end of the list does not start scrolling
      // whatever is behind the glossary.
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;

      // app.css hides every scrollbar app-wide. That suits the game screens; a
      // document this long needs to show how far down it goes.
      scrollbar-width: thin;
      scrollbar-color: @edge-warm var(--background-colour-dark);

      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      &::-webkit-scrollbar-track {
        background: var(--background-colour-dark);
      }
      &::-webkit-scrollbar-thumb {
        background: @edge-mid;
        border-radius: 8px;
      }
      &::-webkit-scrollbar-thumb:hover {
        background: @edge-warm;
      }

      @media @mobile {
        --gutter: 10px;
      }
    }

    // Every section's entry component roots itself on .entry, and they are
    // direct children of .content -- so the spacing between them, the rule
    // that separates them and the hover wash live here once rather than being
    // written out nine times over.
    .content :global(.entry) {
      position: relative;
      // Bled out to the panel edges so the rules and the hover read as bands
      // across the page rather than as another box drawn around the text.
      padding: 20px var(--gutter);
      margin: 0 calc(var(--gutter) * -1);
      transition: background 0.2s;
    }

    // Only ever between two entries: nothing trailing the last one, and
    // nothing wedged under the intro or a filter bar.
    .content :global(.entry + .entry)::before {
      content: '';
      position: absolute;
      top: 0;
      left: var(--gutter);
      right: var(--gutter);
      .glossary-rule();
    }

    .content :global(.entry:hover) {
      background: rgba(232, 215, 174, 0.025);
    }

    .empty {
      padding: 30px 10px;
      text-align: center;
      font-style: italic;
      opacity: 0.6;
    }

    // The lead paragraph of the section, set as the opening of a page rather
    // than as a callout box hanging off the top of a list. Global because Text
    // renders it, so it carries Text's scope rather than this component's.
    :global(.intro) {
      padding: 0 0 18px;
      font-size: 0.92rem;
      line-height: 1.7;
      text-align: left;
      opacity: 0.8;

      &::first-letter {
        float: left;
        margin: 2px 8px 0 0;
        font-size: 2.6em;
        line-height: 0.82;
        color: var(--gold);
        opacity: 0.9;
      }
    }

    .tags {
      display: flex;
      flex-direction: row;
      justify-content: center;
      grid-gap: 10px;
      margin-bottom: 4px;

      .tag {
        grid-gap: 8px;
        padding: 6px 18px;
        font-size: 0.85rem;
        background: var(--background-colour);
        border: 1px solid var(--background-colour-light);
        box-shadow: none;
        opacity: 0.7;
        transition:
          background 0.2s,
          opacity 0.2s,
          border-color 0.2s;

        // How many effects are on each side of the split, so the filter says
        // something before you press it.
        b {
          font-size: 0.75rem;
          opacity: 0.7;
        }

        &:hover {
          opacity: 1;
          filter: none;
        }

        &.selected {
          opacity: 1;
          background: var(--background-colour-light);

          &.boon {
            color: var(--green);
            border-color: var(--green);
          }
          &.bane {
            color: var(--red);
            border-color: var(--red);
          }
        }
      }
    }

    // The landing spot for "View Techniques" on a hero, so it has to say what
    // it has done to the list below it and offer the way back out.
    .die-filter {
      margin-bottom: 6px;
      padding: 10px;
      background: var(--background-colour-dark);
      border: 1px solid @edge-mid;
      border-radius: var(--radius);

      .die-filter-label {
        display: block;
        margin-bottom: 8px;
        font-size: 0.75rem;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        text-align: center;
        opacity: 0.7;
      }
    }

    .die-list {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      grid-gap: 10px;

      .die-container {
        position: relative;
      }

      .clear-die {
        font-size: 0.8rem;
        padding: 8px 16px;
        background: var(--background-colour);
        border: 1px solid var(--background-colour-light);
        box-shadow: none;
        transition:
          border-color 0.2s,
          color 0.2s;

        &:hover,
        &:focus-visible {
          filter: none;
          color: var(--gold);
          border-color: @edge-warm;
        }

        &:active {
          box-shadow: none;
        }
      }
    }
  }
</style>
